import { hasCardStat, sphereAccentClasses } from "../utils/sphereAccent";

function StatChip({ label, value, size }) {
  const compact = size === "tile";
  return (
    <span
      className={`inline-flex items-baseline gap-0.5 rounded border border-slate-600/80 bg-slate-900/90 font-medium tabular-nums text-slate-100 ${
        compact ? "px-2 py-1 text-sm leading-none" : "px-2.5 py-1 text-base leading-tight"
      }`}
    >
      <span className="font-semibold text-slate-500">{label}</span>
      {value}
    </span>
  );
}

/**
 * When card art is missing or failed to load, renders a 5×7 “synthetic” card face
 * so type, sphere, cost, threat, stats, traits, keywords, and rules text stay visible.
 */
export function CardSyntheticFace({ card, variant = "tile", className = "", children }) {
  if (!card) return null;

  const accent = sphereAccentClasses(card.sphere_name);
  const isTile = variant === "tile";
  const textSize = isTile ? "text-sm leading-normal" : "text-base leading-relaxed";
  const titleSize = isTile ? "text-base leading-snug" : "text-xl leading-snug";
  const typeSize = isTile ? "text-sm" : "text-base";
  const metaChip = isTile ? "px-2 py-1 text-sm" : "px-2.5 py-1 text-base";
  const sphereHeader = isTile ? "text-sm" : "text-base";

  const stats = [
    ["W", card.willpower],
    ["A", card.attack],
    ["D", card.defense],
    ["HP", card.health],
  ].filter(([, v]) => hasCardStat(v));

  return (
    <div
      className={`relative aspect-[5/7] w-full overflow-hidden rounded-lg border border-slate-600/80 bg-gradient-to-b from-slate-900 to-slate-950 shadow-inner ${className}`.trim()}
    >
      <div className={`absolute bottom-0 left-0 top-0 w-2 ${accent.bar}`} aria-hidden />
      <div className="absolute inset-0 flex min-h-0 flex-col gap-1.5 pl-3.5 pr-2.5 pt-2.5 pb-2.5 sm:gap-2 sm:pl-4 sm:pr-3 sm:pt-3 sm:pb-3">
        <div className="flex shrink-0 items-start justify-between gap-1.5">
          <span
            className={`min-w-0 truncate font-semibold uppercase tracking-wide ${accent.header} ${sphereHeader}`}
          >
            {card.sphere_name || "—"}
          </span>
          <div className="flex shrink-0 flex-wrap justify-end gap-1">
            {hasCardStat(card.cost) && (
              <span
                className={`rounded border font-semibold tabular-nums text-slate-100 ${accent.chip} ${metaChip}`}
              >
                {card.cost}
              </span>
            )}
            {hasCardStat(card.threat) && (
              <span
                title="Threat"
                className={`rounded border border-amber-600/50 bg-amber-950/70 font-semibold tabular-nums text-amber-100 ${metaChip}`}
              >
                T{card.threat}
              </span>
            )}
          </div>
        </div>

        <div className="min-h-0 shrink-0">
          <h3
            className={`font-bold text-slate-50 ${titleSize} ${isTile ? "line-clamp-2" : "line-clamp-3"}`}
          >
            {card.name}
            {card.is_unique ? <span className="font-normal text-amber-300/90"> *</span> : null}
          </h3>
          {card.type_name && (
            <p className={`mt-0.5 font-medium uppercase tracking-wide text-slate-500 ${typeSize}`}>
              {card.type_name}
            </p>
          )}
        </div>

        {(stats.length > 0 || hasCardStat(card.deck_limit)) && (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {stats.map(([label, val]) => (
              <StatChip key={label} label={label} value={val} size={isTile ? "tile" : "detail"} />
            ))}
            {hasCardStat(card.deck_limit) && (
              <StatChip label="Deck" value={card.deck_limit} size={isTile ? "tile" : "detail"} />
            )}
          </div>
        )}

        {card.traits && (
          <p className={`shrink-0 text-slate-400 ${textSize} ${isTile ? "line-clamp-2" : "line-clamp-3"}`}>
            {card.traits}
          </p>
        )}

        {card.keywords && (
          <p
            className={`shrink-0 text-amber-200/85 ${textSize} ${isTile ? "line-clamp-2" : "line-clamp-3"}`}
          >
            {card.keywords}
          </p>
        )}

        {card.text && (
          <div
            className={`min-h-0 flex-1 overflow-hidden text-slate-300 ${textSize} ${
              isTile ? "[&_p]:my-1" : "[&_p]:my-1.5"
            } max-w-none [&_em]:text-inherit [&_li]:text-inherit [&_p]:text-inherit [&_strong]:text-slate-100`}
          >
            <div
              className={isTile ? "line-clamp-4" : "line-clamp-8"}
              dangerouslySetInnerHTML={{ __html: card.text }}
            />
          </div>
        )}

        {card.flavor && !isTile && (
          <div
            className="line-clamp-3 shrink-0 text-base italic leading-relaxed text-slate-500"
            dangerouslySetInnerHTML={{ __html: card.flavor }}
          />
        )}

        <p
          className={`mt-auto shrink-0 text-center text-slate-600 ${isTile ? "text-xs" : "text-sm"}`}
        >
          No image · stats preview
        </p>
      </div>
      {children}
    </div>
  );
}
